/**
 * Optional inspect sheet for the patch / engraving / apparel builders.
 *
 * Opens a modal copy of the product photo with the artwork overlay so the
 * customer can pinch, drag and double-tap to view placement larger. The copy
 * is inert: gestures here never write back to placement, so drag and pinch on
 * the live overlay remain the only way to move artwork.
 */

import { applyAlignment } from '@patch/alignment';
import { lockScroll, unlockScroll } from '@theme/utilities';

const DESIGNER_SELECTOR = 'patch-designer, engraving-preview, apparel-preview';
const OVERLAY_SELECTOR = 'patch-preview-overlay, live-preview-overlay';
const HOST_SELECTOR = '.product-media.patch-preview-host';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const OPEN_SCALE = 1;
const ZOOMED_SCALE = 2;
const DOUBLE_TAP_MS = 280;
const DOUBLE_TAP_DISTANCE = 40;

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/**
 * @param {HTMLElement} designer
 * @returns {HTMLElement | null}
 */
function findMediaColumn(designer) {
  return designer.closest('.product-information__grid')?.querySelector('.product-information__media') || null;
}

/**
 * @param {Element} host
 * @returns {boolean}
 */
function hasVisibleArtwork(host) {
  for (const overlay of host.querySelectorAll(OVERLAY_SELECTOR)) {
    if (!(overlay instanceof HTMLElement)) continue;
    if (overlay.hidden) continue;
    if (overlay.classList.contains('patch-preview-overlay--empty')) continue;
    if (!overlay.querySelector('svg')) continue;
    return true;
  }

  return false;
}

/**
 * @param {HTMLElement} media
 * @returns {HTMLElement | null}
 */
function findInspectableHost(media) {
  for (const host of media.querySelectorAll(HOST_SELECTOR)) {
    if (!(host instanceof HTMLElement)) continue;
    if (hasVisibleArtwork(host)) return host;
  }

  return null;
}

/**
 * Builds an inert copy of the photo and its overlay.
 *
 * Overlay geometry is stored as percentages of the host photo, so a copy
 * placed in a box with the same aspect ratio lands in the same spot at any
 * size. Cloning the `img` keeps its `srcset`, letting the browser fetch a
 * sharper source for the larger box.
 *
 * @param {HTMLElement} host
 * @returns {{ frame: HTMLElement, aspect: number } | null}
 */
function buildInspectFrame(host) {
  const rect = host.getBoundingClientRect();
  if (rect.width < 8 || rect.height < 8) return null;

  const sourceImage = host.querySelector('img.product-media__image, img');
  if (!(sourceImage instanceof HTMLImageElement)) return null;

  const frame = document.createElement('div');
  frame.className = 'live-preview-inspect__frame';

  const image = /** @type {HTMLImageElement} */ (sourceImage.cloneNode(false));
  const sourceStyle = getComputedStyle(sourceImage);
  image.className = 'live-preview-inspect__photo';
  image.removeAttribute('style');
  image.style.objectFit = sourceStyle.objectFit;
  image.style.objectPosition = sourceStyle.objectPosition;
  image.draggable = false;
  image.sizes = '100vw';
  frame.appendChild(image);

  for (const overlay of host.querySelectorAll(OVERLAY_SELECTOR)) {
    if (!(overlay instanceof HTMLElement)) continue;
    if (overlay.hidden) continue;
    if (overlay.classList.contains('patch-preview-overlay--empty')) continue;
    if (!overlay.querySelector('svg')) continue;

    const clone = /** @type {HTMLElement} */ (overlay.cloneNode(true));
    clone.removeAttribute('ref');
    clone.style.pointerEvents = 'none';
    clone.setAttribute('aria-hidden', 'true');
    frame.appendChild(clone);
  }

  return { frame, aspect: rect.width / rect.height };
}

/**
 * @param {HTMLElement} designer
 */
function readLabels(designer) {
  return {
    open: designer.dataset.inspectLabel || 'View placement larger',
    close: designer.dataset.inspectClose || 'Close',
    hint: designer.dataset.inspectHint || 'Pinch to zoom. Drag to pan.',
    unavailable: designer.dataset.inspectUnavailable || 'Larger view is unavailable right now.',
  };
}

class InspectSheet {
  /** @type {HTMLElement} */
  #media;

  /** @type {ReturnType<typeof readLabels>} */
  #labels;

  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {HTMLElement | null} */
  #stage = null;

  /** @type {HTMLElement | null} */
  #status = null;

  /** @type {HTMLElement | null} */
  #frame = null;

  /** Width / height of the product photo the copy was taken from. */
  #frameAspect = 1;

  /** @type {ResizeObserver | null} */
  #stageResize = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #syncFrame = null;

  /** @type {AbortController | null} */
  #gestures = null;

  #scale = OPEN_SCALE;
  #translateX = 0;
  #translateY = 0;

  /** @type {Map<number, PointerEvent>} */
  #pointers = new Map();

  /** @type {{ x: number, y: number, distance: number, scale: number, translateX: number, translateY: number } | null} */
  #origin = null;

  #lastTapAt = 0;

  /** @type {{ x: number, y: number } | null} */
  #lastTapPoint = null;

  /**
   * @param {HTMLElement} media
   * @param {HTMLElement} designer
   */
  constructor(media, designer) {
    this.#media = media;
    this.#labels = readLabels(designer);

    this.#createButton();

    // The button lives inside the element being watched, so mutations it causes
    // must never feed back into the observer: records from the button are
    // ignored, the sync is coalesced into one frame, and `hidden` is only
    // written when it actually changes.
    this.#observer = new MutationObserver((records) => {
      for (const record of records) {
        if (this.#isOwnMutation(record.target)) continue;
        this.#scheduleSync();
        return;
      }
    });
    this.#observer.observe(media, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'class'],
    });
    this.#syncButton();
  }

  destroy() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#stageResize?.disconnect();
    this.#stageResize = null;

    if (this.#syncFrame !== null) {
      cancelAnimationFrame(this.#syncFrame);
      this.#syncFrame = null;
    }

    this.#gestures?.abort();
    this.#gestures = null;

    if (this.#dialog) {
      if (this.#dialog.open) this.#dialog.close();
      unlockScroll(this.#dialog);
      this.#dialog.remove();
      this.#dialog = null;
    }

    this.#button?.remove();
    this.#button = null;
  }

  #createButton() {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'live-preview-inspect__open';
    button.hidden = true;
    button.setAttribute('aria-label', this.#labels.open);
    button.innerHTML =
      '<svg class="live-preview-inspect__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"><circle cx="10.5" cy="10.5" r="6.25"/><path d="M15.2 15.2 20 20"/><path d="M10.5 8v5M8 10.5h5"/></svg>';
    button.addEventListener('click', () => this.#open());

    this.#media.appendChild(button);
    this.#button = button;
  }

  /**
   * @param {Node} target
   * @returns {boolean}
   */
  #isOwnMutation(target) {
    const button = this.#button;
    if (!button) return false;
    return target === button || button.contains(target);
  }

  #scheduleSync() {
    if (this.#syncFrame !== null) return;

    this.#syncFrame = requestAnimationFrame(() => {
      this.#syncFrame = null;
      this.#syncButton();
    });
  }

  #syncButton() {
    const button = this.#button;
    if (!button) return;

    const hidden = !findInspectableHost(this.#media);
    if (button.hidden !== hidden) button.hidden = hidden;
  }

  #ensureDialog() {
    if (this.#dialog) return this.#dialog;

    const dialog = document.createElement('dialog');
    dialog.className = 'live-preview-inspect';
    dialog.setAttribute('aria-label', this.#labels.open);
    dialog.innerHTML = `
      <button type="button" class="live-preview-inspect__close" aria-label="${escapeHtml(this.#labels.close)}">
        <span aria-hidden="true">&times;</span>
      </button>
      <div class="live-preview-inspect__stage"></div>
      <p class="live-preview-inspect__status" role="status" hidden></p>
      <p class="live-preview-inspect__hint">${escapeHtml(this.#labels.hint)}</p>
    `;

    dialog.querySelector('.live-preview-inspect__close')?.addEventListener('click', () => this.#close());
    dialog.addEventListener('close', () => {
      unlockScroll(dialog);
      this.#gestures?.abort();
      this.#gestures = null;
      this.#pointers.clear();
      this.#origin = null;
      if (this.#stage) this.#stage.replaceChildren();
      this.#frame = null;
    });

    document.body.appendChild(dialog);
    this.#dialog = dialog;
    this.#stage = dialog.querySelector('.live-preview-inspect__stage');
    this.#status = dialog.querySelector('.live-preview-inspect__status');

    if (this.#stage) {
      this.#stageResize = new ResizeObserver(() => this.#fitFrame());
      this.#stageResize.observe(this.#stage);
    }

    return dialog;
  }

  /**
   * Sizes the copy to fill the sheet at the photo's own aspect ratio, so the
   * overlay's percentage placement stays true at any viewport size.
   */
  #fitFrame() {
    const stage = this.#stage;
    const frame = this.#frame;
    if (!stage || !frame) return;

    const { width, height } = stage.getBoundingClientRect();
    if (width < 1 || height < 1) return;

    const scale = Math.min(width / this.#frameAspect, height);
    frame.style.width = `${scale * this.#frameAspect}px`;
    frame.style.height = `${scale}px`;
    for (const overlay of frame.querySelectorAll('[data-patch-perspective-alignment]')) {
      try { applyAlignment(overlay, JSON.parse(overlay.dataset.patchPerspectiveAlignment)); }
      catch { overlay.hidden = true; }
    }
  }

  #open() {
    const host = findInspectableHost(this.#media);
    if (!host) return;

    const dialog = this.#ensureDialog();
    const stage = this.#stage;
    const status = this.#status;
    if (!stage || !status) return;

    const built = buildInspectFrame(host);
    this.#frame = built?.frame ?? null;
    this.#frameAspect = built?.aspect ?? 1;
    this.#resetTransform(OPEN_SCALE);

    if (built) {
      stage.replaceChildren(built.frame);
      status.hidden = true;
      this.#applyTransform();
    } else {
      stage.replaceChildren();
      status.hidden = false;
      status.textContent = this.#labels.unavailable;
    }

    if (!dialog.open) {
      lockScroll(dialog);
      dialog.showModal();
    }

    if (!built) return;

    this.#fitFrame();
    this.#bindGestures(stage);
  }

  #close() {
    const dialog = this.#dialog;
    if (dialog?.open) dialog.close();
  }

  /**
   * @param {HTMLElement} stage
   */
  #bindGestures(stage) {
    this.#gestures?.abort();
    this.#gestures = new AbortController();
    const { signal } = this.#gestures;

    stage.addEventListener(
      'pointerdown',
      (event) => {
        if (event.button !== 0 && event.pointerType === 'mouse') return;

        stage.setPointerCapture(event.pointerId);
        this.#pointers.set(event.pointerId, event);
        this.#origin = this.#measureOrigin();

        if (this.#pointers.size === 1) this.#handleDoubleTap(event);
      },
      { signal }
    );

    stage.addEventListener(
      'pointermove',
      (event) => {
        if (!this.#origin || !this.#pointers.has(event.pointerId)) return;
        this.#pointers.set(event.pointerId, event);

        const points = [...this.#pointers.values()];
        const first = points[0];
        const second = points[1];

        if (first && second) {
          const distance = Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY) || 1;
          const ratio = distance / (this.#origin.distance || distance);
          this.#zoomAround(
            clamp(this.#origin.scale * ratio, MIN_SCALE, MAX_SCALE),
            (first.clientX + second.clientX) / 2,
            (first.clientY + second.clientY) / 2,
            stage
          );
          return;
        }

        const current = this.#pointers.get(event.pointerId);
        if (!current) return;

        this.#translateX = this.#origin.translateX + (current.clientX - this.#origin.x);
        this.#translateY = this.#origin.translateY + (current.clientY - this.#origin.y);
        this.#applyTransform();
      },
      { signal }
    );

    const endPointer = (/** @type {PointerEvent} */ event) => {
      this.#pointers.delete(event.pointerId);

      if (this.#pointers.size > 0) {
        this.#origin = this.#measureOrigin();
        return;
      }

      this.#origin = null;
      if (this.#scale <= MIN_SCALE + 0.01) {
        this.#resetTransform(MIN_SCALE);
        this.#applyTransform();
      }
    };

    stage.addEventListener('pointerup', endPointer, { signal });
    stage.addEventListener('pointercancel', endPointer, { signal });

    stage.addEventListener(
      'wheel',
      (event) => {
        event.preventDefault();
        const factor = event.deltaY < 0 ? 1.08 : 1 / 1.08;
        this.#zoomAround(clamp(this.#scale * factor, MIN_SCALE, MAX_SCALE), event.clientX, event.clientY, stage);
      },
      { passive: false, signal }
    );
  }

  /**
   * Zooms while keeping the point under the fingers or cursor in place.
   *
   * @param {number} nextScale
   * @param {number} clientX
   * @param {number} clientY
   * @param {HTMLElement} stage
   */
  #zoomAround(nextScale, clientX, clientY, stage) {
    const rect = stage.getBoundingClientRect();
    const focusX = clientX - rect.left - rect.width / 2;
    const focusY = clientY - rect.top - rect.height / 2;
    const delta = nextScale / this.#scale;

    this.#translateX = focusX - (focusX - this.#translateX) * delta;
    this.#translateY = focusY - (focusY - this.#translateY) * delta;
    this.#scale = nextScale;
    this.#applyTransform();
  }

  /**
   * @param {PointerEvent} event
   */
  #handleDoubleTap(event) {
    const now = performance.now();
    const previous = this.#lastTapPoint;
    const quick = now - this.#lastTapAt <= DOUBLE_TAP_MS;
    const near =
      previous != null && Math.hypot(event.clientX - previous.x, event.clientY - previous.y) <= DOUBLE_TAP_DISTANCE;

    this.#lastTapAt = now;
    this.#lastTapPoint = { x: event.clientX, y: event.clientY };

    if (!quick || !near) return;

    this.#resetTransform(this.#scale > MIN_SCALE + 0.05 ? MIN_SCALE : ZOOMED_SCALE);
    this.#applyTransform();
    this.#lastTapAt = 0;
    this.#lastTapPoint = null;
  }

  #measureOrigin() {
    const points = [...this.#pointers.values()];
    const first = points[0];
    const second = points[1];

    return {
      x: first?.clientX ?? 0,
      y: first?.clientY ?? 0,
      distance:
        first && second ? Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY) || 1 : 1,
      scale: this.#scale,
      translateX: this.#translateX,
      translateY: this.#translateY,
    };
  }

  /**
   * @param {number} scale
   */
  #resetTransform(scale) {
    this.#scale = scale;
    this.#translateX = 0;
    this.#translateY = 0;
  }

  #applyTransform() {
    if (!this.#frame) return;
    this.#frame.style.transform = `translate3d(${this.#translateX}px, ${this.#translateY}px, 0) scale(${this.#scale})`;
  }
}

function init() {
  /** @type {Map<HTMLElement, InspectSheet>} */
  const sheets = new Map();

  const syncAll = () => {
    for (const designer of document.querySelectorAll(DESIGNER_SELECTOR)) {
      if (!(designer instanceof HTMLElement)) continue;
      if (sheets.has(designer)) continue;

      const media = findMediaColumn(designer);
      if (!media) continue;

      sheets.set(designer, new InspectSheet(media, designer));
    }

    for (const [designer, sheet] of sheets) {
      if (document.contains(designer)) continue;
      sheet.destroy();
      sheets.delete(designer);
    }
  };

  syncAll();
  document.addEventListener('shopify:section:load', syncAll);
  document.addEventListener('shopify:section:unload', syncAll);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
